/*
 * Strings for the work order feature (features/cmmsWorkOrders) — list page, data grid,
 * details panel and all dialogs/tab lists.
 *
 * Shared verbs (add/edit/delete/save/cancel), work order action labels and the enum
 * label helpers live in features/cmms/cmmsMessages.js.
 */
import { defineMessages } from "react-intl";

export const scope = "axionoee.features.cmms.workorders";

export default defineMessages({
  // page
  pageSubtitle: {
    id: `${scope}.page.subtitle`,
    defaultMessage: "Track maintenance work orders, escalations, and resource assignments.",
  },
  addWorkOrder: { id: `${scope}.page.addWorkOrder`, defaultMessage: "Add work order" },

  // shared field labels
  fieldStatus: { id: `${scope}.field.status`, defaultMessage: "Status" },
  fieldType: { id: `${scope}.field.type`, defaultMessage: "Type" },
  fieldPriority: { id: `${scope}.field.priority`, defaultMessage: "Priority" },
  fieldTitle: { id: `${scope}.field.title`, defaultMessage: "Title" },
  fieldTitleRequired: { id: `${scope}.field.titleRequired`, defaultMessage: "Title *" },
  fieldTitleRequiredError: { id: `${scope}.field.titleRequiredError`, defaultMessage: "Title is required" },
  fieldDescription: { id: `${scope}.field.description`, defaultMessage: "Description" },
  fieldDescriptionRequired: { id: `${scope}.field.descriptionRequired`, defaultMessage: "Description *" },
  fieldDescriptionOptional: { id: `${scope}.field.descriptionOptional`, defaultMessage: "Description (optional)" },
  fieldAsset: { id: `${scope}.field.asset`, defaultMessage: "Asset" },
  fieldAssetRequired: { id: `${scope}.field.assetRequired`, defaultMessage: "Asset *" },
  /*
   * The two tools a work order can name, and they mean different things.
   *
   * `fieldTool` is what closing the order books a service against. `fieldReportedTool` is what
   * merely sat in the machine when the fault was reported, which is descriptive: closing books
   * nothing against it. Two labels rather than one, because collapsing them would tell an
   * operator that a reported tool is about to be serviced.
   */
  fieldTool: { id: `${scope}.field.tool`, defaultMessage: "Tool" },
  fieldReportedTool: { id: `${scope}.field.reportedTool`, defaultMessage: "Reported tool" },
  fieldAssignedDepartment: { id: `${scope}.field.assignedDepartment`, defaultMessage: "Assigned department" },
  fieldAssignedDepartmentShort: { id: `${scope}.field.assignedDepartmentShort`, defaultMessage: "Assigned dept." },
  fieldAssignedResource: { id: `${scope}.field.assignedResource`, defaultMessage: "Assigned resource" },
  fieldAssignedTo: { id: `${scope}.field.assignedTo`, defaultMessage: "Assigned to" },
  fieldAssigned: { id: `${scope}.field.assigned`, defaultMessage: "Assigned" },
  fieldDispatchedTo: { id: `${scope}.field.dispatchedTo`, defaultMessage: "Dispatched to" },
  // The crew that gets the work (AB#1624). The department beside it is where the work is.
  fieldTrade: { id: `${scope}.field.trade`, defaultMessage: "Trade" },
  // A retired trade in a picker or in the list's filter. It is only there because orders still
  // carry it, so the name has to say why it is different from the others.
  fieldTradeRetired: { id: `${scope}.field.tradeRetired`, defaultMessage: "{name} (inactive)" },
  fieldPlannedStart: { id: `${scope}.field.plannedStart`, defaultMessage: "Planned start" },
  fieldPlannedEnd: { id: `${scope}.field.plannedEnd`, defaultMessage: "Planned end" },
  // No "(h)" here, unlike the form's label: the value beside it reads "2h 30m" and carries its unit.
  fieldEstimatedHours: { id: `${scope}.field.estimatedHours`, defaultMessage: "Estimated time" },
  fieldActualStart: { id: `${scope}.field.actualStart`, defaultMessage: "Actual start" },
  fieldActualEnd: { id: `${scope}.field.actualEnd`, defaultMessage: "Actual end" },
  fieldClosedAt: { id: `${scope}.field.closedAt`, defaultMessage: "Closed at" },
  fieldCreated: { id: `${scope}.field.created`, defaultMessage: "Created" },
  fieldFailureCode: { id: `${scope}.field.failureCode`, defaultMessage: "Failure code" },
  fieldFailureCauseNote: { id: `${scope}.field.failureCauseNote`, defaultMessage: "Failure cause note" },
  fieldCauseNote: { id: `${scope}.field.causeNote`, defaultMessage: "Cause note" },
  fieldSortOrder: { id: `${scope}.field.sortOrder`, defaultMessage: "Sort order" },
  fieldNotes: { id: `${scope}.field.notes`, defaultMessage: "Notes" },
  fieldLinkedAsset: { id: `${scope}.field.linkedAsset`, defaultMessage: "Linked asset" },

  // data grid
  columnId: { id: `${scope}.column.id`, defaultMessage: "ID" },

  // details panel
  closePanel: { id: `${scope}.details.closePanel`, defaultMessage: "Close panel" },
  changeStatus: { id: `${scope}.details.changeStatus`, defaultMessage: "Change status" },
  apply: { id: `${scope}.details.apply`, defaultMessage: "Apply" },
  claimConfirmTitle: { id: `${scope}.details.claimConfirmTitle`, defaultMessage: "Claim work order" },
  claimConfirmMessage: { id: `${scope}.details.claimConfirmMessage`, defaultMessage: "Do you want to take this job?" },
  tradeInactive: { id: `${scope}.details.tradeInactive`, defaultMessage: "Inactive" },
  // What a retired trade on an order means in practice: the notification fallback from step 4 of
  // AB#1624, and what to do about it.
  tradeInactiveHint: {
    id: `${scope}.details.tradeInactiveHint`,
    defaultMessage: "This trade has been deactivated, so the work order's notifications go to the department's technicians instead. Edit the work order to give it an active trade.",
  },
  tabTasks: { id: `${scope}.tab.tasks`, defaultMessage: "Tasks" },
  tabComments: { id: `${scope}.tab.comments`, defaultMessage: "Comments" },
  tabAttachments: { id: `${scope}.tab.attachments`, defaultMessage: "Attachments" },
  tabMaterials: { id: `${scope}.tab.materials`, defaultMessage: "Materials" },
  tabLabor: { id: `${scope}.tab.labor`, defaultMessage: "Labor" },
  tabDocuments: { id: `${scope}.tab.documents`, defaultMessage: "Documents" },
  tabHistory: { id: `${scope}.tab.history`, defaultMessage: "History" },

  // history tab
  historyEmpty: { id: `${scope}.history.empty`, defaultMessage: "Nothing has happened to this work order yet." },
  historyLoadFailed: { id: `${scope}.history.loadFailed`, defaultMessage: "Could not load the history" },
  historyStatusChange: {
    id: `${scope}.history.statusChange`,
    defaultMessage: "{from} → {to}",
  },
  historyEscalated: {
    id: `${scope}.history.escalated`,
    defaultMessage: "Escalated: {from} → {to}",
  },
  historyUnknownDepartment: { id: `${scope}.history.unknownDepartment`, defaultMessage: "Department {id}" },
  historyNoDepartment: { id: `${scope}.history.noDepartment`, defaultMessage: "No department" },
  historyNoTrade: { id: `${scope}.history.noTrade`, defaultMessage: "No trade" },
  // Under a department hand-over when the trade moved with it.
  historyTradeChanged: { id: `${scope}.history.tradeChanged`, defaultMessage: "Trade: {from} → {to}" },
  historyBy: { id: `${scope}.history.by`, defaultMessage: "by {name}" },

  toastStatusUpdated: { id: `${scope}.toast.statusUpdated`, defaultMessage: "Status updated" },
  toastCheckedIn: { id: `${scope}.toast.checkedIn`, defaultMessage: "Checked in" },
  toastCheckedOut: { id: `${scope}.toast.checkedOut`, defaultMessage: "Checked out" },
  toastClaimed: { id: `${scope}.toast.claimed`, defaultMessage: "You took the job" },
  operationFailed: { id: `${scope}.error.operationFailed`, defaultMessage: "Operation failed" },

  // list page — delete
  deleteWorkOrderTitle: { id: `${scope}.delete.title`, defaultMessage: "Delete work order" },
  deleteWorkOrderMessage: {
    id: `${scope}.delete.message`,
    defaultMessage: "Delete \"{title}\"? This cannot be undone.",
  },
  toastWorkOrderDeleted: { id: `${scope}.toast.workOrderDeleted`, defaultMessage: "Work order deleted" },

  // work order form dialog
  addWorkOrderTitle: { id: `${scope}.form.addTitle`, defaultMessage: "Add work order" },
  editWorkOrderTitle: { id: `${scope}.form.editTitle`, defaultMessage: "Edit work order" },

  // comments
  commentsEmpty: { id: `${scope}.comments.empty`, defaultMessage: "No comments yet." },
  commentsPlaceholder: { id: `${scope}.comments.placeholder`, defaultMessage: "Add a comment..." },
  commentsEditAria: { id: `${scope}.comments.editAria`, defaultMessage: "Edit comment" },
  commentsDeleteAria: { id: `${scope}.comments.deleteAria`, defaultMessage: "Delete comment" },
  commentsDeleteTitle: { id: `${scope}.comments.deleteTitle`, defaultMessage: "Delete comment" },
  commentsDeleteMessage: { id: `${scope}.comments.deleteMessage`, defaultMessage: "Delete this comment?" },
  toastCommentAdded: { id: `${scope}.toast.commentAdded`, defaultMessage: "Comment added" },
  toastCommentUpdated: { id: `${scope}.toast.commentUpdated`, defaultMessage: "Comment updated" },
  toastCommentDeleted: { id: `${scope}.toast.commentDeleted`, defaultMessage: "Comment deleted" },
  commentsSaveFailed: { id: `${scope}.comments.saveFailed`, defaultMessage: "Could not save comment" },

  // attachments
  attachmentsEmpty: { id: `${scope}.attachments.empty`, defaultMessage: "No attachments yet." },
  attachmentsUpload: { id: `${scope}.attachments.upload`, defaultMessage: "Upload file" },
  attachmentsColumnFile: { id: `${scope}.attachments.column.file`, defaultMessage: "File" },
  attachmentsColumnUploadedBy: { id: `${scope}.attachments.column.uploadedBy`, defaultMessage: "Uploaded by" },
  attachmentsColumnUploadedAt: { id: `${scope}.attachments.column.uploadedAt`, defaultMessage: "Uploaded at" },
  attachmentsDownloadAria: { id: `${scope}.attachments.downloadAria`, defaultMessage: "Download" },
  attachmentsDeleteAria: { id: `${scope}.attachments.deleteAria`, defaultMessage: "Delete" },
  attachmentsDeleteTitle: { id: `${scope}.attachments.deleteTitle`, defaultMessage: "Delete attachment" },
  attachmentsDeleteMessage: { id: `${scope}.attachments.deleteMessage`, defaultMessage: "Delete \"{fileName}\"?" },
  attachmentsTooLarge: {
    id: `${scope}.attachments.tooLarge`,
    defaultMessage: "{name} is too large. The maximum upload size is {max}.",
  },
  attachmentsTypeNotAllowed: {
    id: `${scope}.attachments.typeNotAllowed`,
    defaultMessage: "{name} is a {type} file, which is not an accepted attachment type.",
  },
  attachmentsTypeUnknown: { id: `${scope}.attachments.typeUnknown`, defaultMessage: "unknown" },
  attachmentsProgress: { id: `${scope}.attachments.progress`, defaultMessage: "Uploading… {percent}%" },
  toastFileUploaded: { id: `${scope}.toast.fileUploaded`, defaultMessage: "File uploaded" },
  toastAttachmentDeleted: { id: `${scope}.toast.attachmentDeleted`, defaultMessage: "Attachment deleted" },

  // tasks
  tasksEmpty: { id: `${scope}.tasks.empty`, defaultMessage: "No tasks yet." },
  tasksAdd: { id: `${scope}.tasks.add`, defaultMessage: "Add task" },
  tasksEditTitle: { id: `${scope}.tasks.editTitle`, defaultMessage: "Edit task" },
  tasksDeleteTitle: { id: `${scope}.tasks.deleteTitle`, defaultMessage: "Delete task" },
  tasksDeleteMessage: { id: `${scope}.tasks.deleteMessage`, defaultMessage: "Delete \"{title}\"?" },
  toastTaskCreated: { id: `${scope}.toast.taskCreated`, defaultMessage: "Task created" },
  toastTaskUpdated: { id: `${scope}.toast.taskUpdated`, defaultMessage: "Task updated" },
  toastTaskDeleted: { id: `${scope}.toast.taskDeleted`, defaultMessage: "Task deleted" },
  tasksLoadFailed: { id: `${scope}.tasks.loadFailed`, defaultMessage: "Could not load tasks" },
  tasksSaveFailed: { id: `${scope}.tasks.saveFailed`, defaultMessage: "Could not save task" },

  // materials
  materialsEmpty: { id: `${scope}.materials.empty`, defaultMessage: "No materials logged yet." },
  materialsAdd: { id: `${scope}.materials.add`, defaultMessage: "Add material" },
  materialsEditTitle: { id: `${scope}.materials.editTitle`, defaultMessage: "Edit material" },
  materialsDeleteTitle: { id: `${scope}.materials.deleteTitle`, defaultMessage: "Delete material" },
  materialsDeleteMessage: { id: `${scope}.materials.deleteMessage`, defaultMessage: "Delete \"{description}\"?" },
  materialsPartNumber: { id: `${scope}.materials.partNumber`, defaultMessage: "Part number" },
  materialsQuantityRequired: { id: `${scope}.materials.quantityRequired`, defaultMessage: "Quantity *" },
  materialsUnit: { id: `${scope}.materials.unit`, defaultMessage: "Unit" },
  toastMaterialAdded: { id: `${scope}.toast.materialAdded`, defaultMessage: "Material added" },
  toastMaterialUpdated: { id: `${scope}.toast.materialUpdated`, defaultMessage: "Material updated" },
  toastMaterialDeleted: { id: `${scope}.toast.materialDeleted`, defaultMessage: "Material deleted" },
  materialsLoadFailed: { id: `${scope}.materials.loadFailed`, defaultMessage: "Could not load materials" },
  materialsSaveFailed: { id: `${scope}.materials.saveFailed`, defaultMessage: "Could not save material" },

  // materials: register vs free text
  materialsSource: { id: `${scope}.materials.source`, defaultMessage: "Source" },
  materialsSourceRegistered: { id: `${scope}.materials.sourceRegistered`, defaultMessage: "From the register" },
  materialsSourceFreeText: { id: `${scope}.materials.sourceFreeText`, defaultMessage: "Free text" },
  materialsSparePart: { id: `${scope}.materials.sparePart`, defaultMessage: "Spare part" },
  materialsSparePartRequired: {
    id: `${scope}.materials.sparePartRequired`,
    defaultMessage: "Pick a spare part, or switch to free text",
  },
  materialsFromRegister: {
    id: `${scope}.materials.fromRegister`,
    defaultMessage: "From the register: {summary}",
  },
  materialsOverrideToggle: {
    id: `${scope}.materials.overrideToggle`,
    defaultMessage: "Override description, unit or cost",
  },
  materialsOverrideHint: {
    id: `${scope}.materials.overrideHint`,
    defaultMessage: "Leave blank to use the register's values. Anything typed here is kept as written.",
  },
  materialsRegisteredRow: {
    id: `${scope}.materials.registeredRow`,
    defaultMessage: "Booked against the register — counts against stock",
  },
  materialsRegisterEmpty: {
    id: `${scope}.materials.registerEmpty`,
    defaultMessage: "No spare parts in the register yet. Use free text, or add the part first.",
  },
  materialsRegisterLoadFailed: {
    id: `${scope}.materials.registerLoadFailed`,
    defaultMessage: "Could not load the spare parts register",
  },
  materialsShortfall: {
    id: `${scope}.materials.shortfall`,
    defaultMessage: "Only {onHand} on hand. Booking {wanted} takes the balance to {resulting} — saved as recorded.",
  },
  materialsUnitRequired: { id: `${scope}.materials.unitRequired`, defaultMessage: "Unit is required" },
  materialsUnitCostInvalid: { id: `${scope}.materials.unitCostInvalid`, defaultMessage: "Unit cost cannot be negative" },

  // labor hours
  laborEmpty: { id: `${scope}.labor.empty`, defaultMessage: "No labor hours yet." },
  laborEditTitle: { id: `${scope}.labor.editTitle`, defaultMessage: "Edit labor hour" },
  laborCheckInRequired: { id: `${scope}.labor.checkInRequired`, defaultMessage: "Check-in *" },
  laborCheckOut: { id: `${scope}.labor.checkOut`, defaultMessage: "Check-out" },
  laborActive: { id: `${scope}.labor.active`, defaultMessage: "Active" },
  laborTotal: { id: `${scope}.labor.total`, defaultMessage: "Total: {duration}" },
  laborCheckInRequiredError: { id: `${scope}.labor.checkInRequiredError`, defaultMessage: "Check-in is required" },
  laborCheckOutAfterCheckIn: {
    id: `${scope}.labor.checkOutAfterCheckIn`,
    defaultMessage: "Check-out must be after check-in",
  },
  toastLaborUpdated: { id: `${scope}.toast.laborUpdated`, defaultMessage: "Labor hour updated" },
  laborLoadFailed: { id: `${scope}.labor.loadFailed`, defaultMessage: "Could not load labor hours" },
  laborSaveFailed: { id: `${scope}.labor.saveFailed`, defaultMessage: "Could not save labor hour" },

  // related documents
  relatedDocumentsEmpty: { id: `${scope}.relatedDocuments.empty`, defaultMessage: "No related documents yet." },
  relatedDocumentsAdd: { id: `${scope}.relatedDocuments.add`, defaultMessage: "Add document" },
  relatedDocumentsDialogTitle: { id: `${scope}.relatedDocuments.dialogTitle`, defaultMessage: "Add related document" },
  relatedDocumentsDeleteTitle: { id: `${scope}.relatedDocuments.deleteTitle`, defaultMessage: "Delete document" },
  relatedDocumentsDeleteMessage: { id: `${scope}.relatedDocuments.deleteMessage`, defaultMessage: "Delete \"{title}\"?" },
  relatedDocumentsExternalUrl: { id: `${scope}.relatedDocuments.externalUrl`, defaultMessage: "External URL" },
  relatedDocumentsInternalAttachment: { id: `${scope}.relatedDocuments.internalAttachment`, defaultMessage: "Internal attachment" },
  relatedDocumentsUrlRequired: { id: `${scope}.relatedDocuments.urlRequired`, defaultMessage: "URL *" },
  relatedDocumentsAttachmentRequired: { id: `${scope}.relatedDocuments.attachmentRequired`, defaultMessage: "Attachment *" },
  toastDocumentLinked: { id: `${scope}.toast.documentLinked`, defaultMessage: "Document linked" },
  toastDocumentRemoved: { id: `${scope}.toast.documentRemoved`, defaultMessage: "Document removed" },
  relatedDocumentsLoadFailed: { id: `${scope}.relatedDocuments.loadFailed`, defaultMessage: "Could not load documents" },
  relatedDocumentsAddFailed: { id: `${scope}.relatedDocuments.addFailed`, defaultMessage: "Could not add document" },

  laborManualOverride: { id: `${scope}.labor.manualOverride`, defaultMessage: "manual" },
  gridEditAria: { id: `${scope}.grid.editAria`, defaultMessage: "Edit work order" },
  gridDeleteAria: { id: `${scope}.grid.deleteAria`, defaultMessage: "Delete work order" },

  // section headings with counts
  attachmentsCount: { id: `${scope}.attachments.count`, defaultMessage: "Attachments ({count})" },
  commentsCount: { id: `${scope}.comments.count`, defaultMessage: "Comments ({count})" },
  tasksCount: { id: `${scope}.tasks.count`, defaultMessage: "Tasks ({count})" },
  materialsCount: { id: `${scope}.materials.count`, defaultMessage: "Materials ({count})" },
  laborCount: { id: `${scope}.labor.count`, defaultMessage: "Labor hours ({count})" },
  relatedDocumentsCount: { id: `${scope}.relatedDocuments.count`, defaultMessage: "Documents ({count})" },
  commentEdited: { id: `${scope}.comments.edited`, defaultMessage: "(edited)" },

  // additional column + form labels found during migration
  columnPartNumberShort: { id: `${scope}.column.partNumberShort`, defaultMessage: "Part #" },
  columnUsedBy: { id: `${scope}.column.usedBy`, defaultMessage: "Used by" },
  columnLink: { id: `${scope}.column.link`, defaultMessage: "Link" },
  columnAdded: { id: `${scope}.column.added`, defaultMessage: "Added" },
  columnUser: { id: `${scope}.column.user`, defaultMessage: "User" },
  columnCheckIn: { id: `${scope}.column.checkIn`, defaultMessage: "Check-in" },
  columnCheckOut: { id: `${scope}.column.checkOut`, defaultMessage: "Check-out" },
  filterAll: { id: `${scope}.filter.all`, defaultMessage: "All" },
  filterClear: { id: `${scope}.filter.clear`, defaultMessage: "Clear filters" },
  // "Mine" is GetMineAsync's definition — dispatched to me, assigned to me, or Open in one of my
  // departments or trades. "Assigned to me" would name only the middle third of that.
  filterMine: { id: `${scope}.filter.mine`, defaultMessage: "My work orders" },
  // Names five of the eight fields it covers. Priority, type and trade are searchable too, but a
  // label that lists everything stops being one anyone reads.
  filterSearch: {
    id: `${scope}.filter.search`,
    defaultMessage: "Search by title, description, asset, assignee or status",
  },
  // The list's from–to filter (AB#1638). The three dates it offers are the grid's own column
  // headers, fieldPlannedStart, fieldPlannedEnd and fieldCreated, so they are not repeated here.
  dateFilterField: { id: `${scope}.dateFilter.field`, defaultMessage: "Date" },
  dateFilterFrom: { id: `${scope}.dateFilter.from`, defaultMessage: "From" },
  dateFilterTo: { id: `${scope}.dateFilter.to`, defaultMessage: "To" },
  // Under the To field when it lies before From.
  dateFilterReversed: { id: `${scope}.dateFilter.reversed`, defaultMessage: "Before the From date" },
  loadFailed: { id: `${scope}.error.loadFailed`, defaultMessage: "Could not load work orders" },
  // Two separate empty states: nothing exists yet vs. the filters excluded everything.
  // Collapsing them into one message sends the user hunting for data the filters hid.
  emptyTitle: { id: `${scope}.empty.title`, defaultMessage: "No work orders yet" },
  emptyHint: {
    id: `${scope}.empty.hint`,
    defaultMessage: "Report a problem from a machine page, or add a work order here.",
  },
  emptyFilteredTitle: { id: `${scope}.empty.filteredTitle`, defaultMessage: "No work orders match the filters" },
  emptyFilteredHint: {
    id: `${scope}.empty.filteredHint`,
    defaultMessage: "Try a different status, type or priority.",
  },

  /*
   * The machine page's work order tab.
   *
   * It gets its own empty state rather than reusing emptyHint, whose call to action is
   * "report a problem from a machine page". On a machine page that sends the reader where
   * they already are.
   */
  /*
   * The tab is named for what it holds, not for one of the things in it. It also carries the
   * mounted tool's service status, so "Work orders" had become narrower than
   * the tab. The word matches the CMMS sidebar entry so the module is called one thing.
   *
   * The empty states below still say "work orders": the list really is work orders, and only
   * the tab's own name widened.
   */
  machineTabLabel: { id: `${scope}.machineTab.label`, defaultMessage: "Maintenance" },
  machineTabEmptyTitle: {
    id: `${scope}.machineTab.emptyTitle`,
    defaultMessage: "No work orders for this machine",
  },
  machineTabEmptyHint: {
    id: `${scope}.machineTab.emptyHint`,
    defaultMessage: "Use the report-problem button above to raise one.",
  },
  machineTabNotLinkedTitle: {
    id: `${scope}.machineTab.notLinkedTitle`,
    defaultMessage: "Machine is not linked to the maintenance system",
  },
  machineTabNotLinkedHint: {
    id: `${scope}.machineTab.notLinkedHint`,
    defaultMessage: "An administrator links a machine to an asset in the asset hierarchy.",
  },
  // A whole sentence with the tool as a parameter, not "Tool:" plus a value — word order and
  // the position of the name differ between languages.
  machineTabToolHeading: {
    id: `${scope}.machineTab.toolHeading`,
    defaultMessage: "Tool {tool} is mounted in this machine",
  },
  // Upper-cased by CSS, not by the message — see the note in cmmsRecurringSchedules/messages.js.
  statTotal: { id: `${scope}.stat.total`, defaultMessage: "Total" },

  // Priority banner. The count is a message rather than the number glued to the label
  // in JSX: both languages happen to put the number first, and a locale that does not is exactly
  // why this is translatable at all.
  priorityBannerTitle: { id: `${scope}.priorityBanner.title`, defaultMessage: "Open by priority" },
  priorityBannerCount: { id: `${scope}.priorityBanner.count`, defaultMessage: "{count} {priority}" },

  // Type tile, in the same two-argument shape as the priority banner above. "All" is doing real
  // work in that title: the tile next to it counts open orders and this one counts every order
  // ever raised, and the two sit side by side.
  typeCountsTitle: { id: `${scope}.typeCounts.title`, defaultMessage: "All by type" },
  typeCountsCount: { id: `${scope}.typeCounts.count`, defaultMessage: "{count} {type}" },
  sectionDetails: { id: `${scope}.section.details`, defaultMessage: "Details" },
  sectionFailure: { id: `${scope}.section.failure`, defaultMessage: "Failure" },
  sectionActions: { id: `${scope}.section.actions`, defaultMessage: "Actions" },
  relatedDocumentsDocumentType: { id: `${scope}.relatedDocuments.documentType`, defaultMessage: "Document type" },
  relatedDocumentsLinkType: { id: `${scope}.relatedDocuments.linkType`, defaultMessage: "Link type" },
  relatedDocumentsExternal: { id: `${scope}.relatedDocuments.external`, defaultMessage: "External" },
  relatedDocumentsAttachmentFallback: {
    id: `${scope}.relatedDocuments.attachmentFallback`,
    defaultMessage: "Attachment #{id}",
  },
  columnDuration: { id: `${scope}.column.duration`, defaultMessage: "Duration" },
  columnQuantityShort: { id: `${scope}.column.quantityShort`, defaultMessage: "Qty" },
  materialsUnitCost: { id: `${scope}.materials.unitCost`, defaultMessage: "Unit cost" },
  materialsTotalCost: { id: `${scope}.materials.totalCost`, defaultMessage: "Total cost: {amount}" },
  materialsAddDialogTitle: { id: `${scope}.materials.addDialogTitle`, defaultMessage: "Add material usage" },
  materialsEditDialogTitle: { id: `${scope}.materials.editDialogTitle`, defaultMessage: "Edit material usage" },
  materialsDescriptionRequired: {
    id: `${scope}.materials.descriptionRequired`,
    defaultMessage: "Description is required",
  },
  materialsQuantityPositive: {
    id: `${scope}.materials.quantityPositive`,
    defaultMessage: "Quantity must be greater than 0",
  },
  relatedDocumentsTitleRequired: {
    id: `${scope}.relatedDocuments.titleRequired`,
    defaultMessage: "Title is required",
  },
  relatedDocumentsUrlRequiredError: {
    id: `${scope}.relatedDocuments.urlRequiredError`,
    defaultMessage: "URL is required",
  },
  relatedDocumentsUrlInvalidError: {
    id: `${scope}.relatedDocuments.urlInvalidError`,
    defaultMessage: "Enter a full web address starting with http:// or https://",
  },
  relatedDocumentsAttachmentRequiredError: {
    id: `${scope}.relatedDocuments.attachmentRequiredError`,
    defaultMessage: "Attachment is required",
  },

  // escalate dialog
  escalateDialogTitle: { id: `${scope}.escalate.title`, defaultMessage: "Escalate work order" },
  escalateIntro: {
    id: `${scope}.escalate.intro`,
    defaultMessage: "The work order moves to the department or trade you choose and reopens there. Choose only a trade and it stays in its department. Comments, attachments, hours and materials stay on it, so the next team can see what has already been tried.",
  },
  escalateTargetDepartment: { id: `${scope}.escalate.targetDepartment`, defaultMessage: "To department" },
  escalateTargetTrade: { id: `${scope}.escalate.targetTrade`, defaultMessage: "To trade" },
  escalateReason: { id: `${scope}.escalate.reason`, defaultMessage: "What have you tried, and why hand it over? *" },
  escalateTargetRequired: {
    id: `${scope}.escalate.targetRequired`,
    defaultMessage: "Choose a department, a trade or both",
  },
  escalateSameTrade: {
    id: `${scope}.escalate.sameTrade`,
    defaultMessage: "The work order is already with that trade — choose another, or a department",
  },
  escalateReasonRequired: {
    id: `${scope}.escalate.reasonRequired`,
    defaultMessage: "A reason is required — it becomes the first comment the next team reads",
  },
  toastWorkOrderEscalated: { id: `${scope}.toast.workOrderEscalated`, defaultMessage: "Work order escalated" },
  escalateFailed: { id: `${scope}.escalate.failed`, defaultMessage: "Could not escalate" },

  // realtime notifications
  notificationCreated: {
    id: `${scope}.notification.created`,
    defaultMessage: "New work order: {title}",
  },
  notificationReopened: {
    id: `${scope}.notification.reopened`,
    defaultMessage: "Work order reopened: {title}",
  },

  // reopen dialog
  reopenDialogTitle: { id: `${scope}.reopen.title`, defaultMessage: "Reopen work order" },
  reopenIntro: {
    id: `${scope}.reopen.intro`,
    defaultMessage: "Reopening sets the status back to Open. The reason is saved as a comment and the assigned technician is kept.",
  },
  reopenReason: { id: `${scope}.reopen.reason`, defaultMessage: "Reason" },
  toastWorkOrderReopened: { id: `${scope}.toast.workOrderReopened`, defaultMessage: "Work order reopened" },
  reopenFailed: { id: `${scope}.reopen.failed`, defaultMessage: "Could not reopen the work order" },

  // list / week / month toggle (AB#1618)
  viewToggleAria: { id: `${scope}.view.toggleAria`, defaultMessage: "View" },
  viewList: { id: `${scope}.view.list`, defaultMessage: "List" },
  viewWeek: { id: `${scope}.view.week`, defaultMessage: "Week overview" },
  viewMonth: { id: `${scope}.view.month`, defaultMessage: "Month overview" },

  // week overview. The range is the locale's own date range ("12–18 Oct"), so only the week
  // number and the joining are ours to word.
  weekLabel: { id: `${scope}.week.label`, defaultMessage: "Week {weekNumber} · {range}" },
  weekPrevious: { id: `${scope}.week.previous`, defaultMessage: "Previous week" },
  weekNext: { id: `${scope}.week.next`, defaultMessage: "Next week" },
  weekCurrent: { id: `${scope}.week.current`, defaultMessage: "This week" },
  weekGroupByAria: { id: `${scope}.week.groupByAria`, defaultMessage: "Group by" },
  weekGroupByDepartment: { id: `${scope}.week.groupByDepartment`, defaultMessage: "Department" },
  weekGroupByTrade: { id: `${scope}.week.groupByTrade`, defaultMessage: "Trade" },
  // The group for orders with nothing to group them by. Named, because those orders are still
  // this week's work.
  weekNoDepartment: { id: `${scope}.week.noDepartment`, defaultMessage: "No department" },
  weekNoTrade: { id: `${scope}.week.noTrade`, defaultMessage: "No trade" },
  // Plurals rather than a number glued to a word: Swedish inflects both ("1 akut", "2 akuta").
  weekCounts: {
    id: `${scope}.week.counts`,
    defaultMessage: "{acute, plural, one {# acute} other {# acute}} · {planned, plural, one {# planned} other {# planned}}",
  },
  // What a group or the whole week is estimated to take. {hours} comes formatted, as "60h 00m".
  weekEstimatedHours: { id: `${scope}.week.estimatedHours`, defaultMessage: "{hours} estimated" },
  // Next to the sum whenever orders are missing from it, so the sum is not read as the whole load.
  weekWithoutEstimate: {
    id: `${scope}.week.withoutEstimate`,
    defaultMessage: "{count, plural, one {# without estimate} other {# without estimate}}",
  },
  weekWithoutEstimateTooltip: {
    id: `${scope}.week.withoutEstimateTooltip`,
    defaultMessage: "Not in the sum: these orders have no estimate yet",
  },
  // What one day of one group holds, under the day's name. Akut and planned together.
  weekDayCount: {
    id: `${scope}.week.dayCount`,
    defaultMessage: "{count, plural, one {# order} other {# orders}}",
  },
  // On a day's hours when orders are missing from them. Counted per day, since a day is small
  // enough that the number says more than the general rule does.
  weekDayWithoutEstimateTooltip: {
    id: `${scope}.week.dayWithoutEstimateTooltip`,
    defaultMessage: "{count, plural, one {# order has no estimate and is not in the sum} other {# orders have no estimate and are not in the sum}}",
  },
  // The header of the column before Monday that holds late work. It names the work in it, not
  // one order, so Swedish has it neuter ("Försenat").
  weekOverdue: { id: `${scope}.week.overdue`, defaultMessage: "Overdue" },
  // "Not done yet" rather than "open", because a late order a schedule has not raised yet is
  // carried as well.
  weekOverdueTooltip: {
    id: `${scope}.week.overdueTooltip`,
    defaultMessage: "From an earlier week and not done yet, so it is this week's work too",
  },
  // An order a recurring schedule has not raised yet, in the badge slot where a status would be.
  weekProjected: { id: `${scope}.week.projected`, defaultMessage: "Upcoming" },
  // {day} comes formatted, as "Monday, October 19".
  weekProjectedTooltip: {
    id: `${scope}.week.projectedTooltip`,
    defaultMessage: "Not created yet. The recurring schedule creates this work order on {day}.",
  },
  weekProjectedOverdueTooltip: {
    id: `${scope}.week.projectedOverdueTooltip`,
    defaultMessage: "Not created yet. It is due, and the recurring schedule creates it at the next nightly run.",
  },
  weekProjectedLoadFailed: {
    id: `${scope}.week.projectedLoadFailed`,
    defaultMessage: "Could not fetch upcoming work from the recurring schedules. The week shows only work orders that already exist.",
  },
  weekEmptyTitle: { id: `${scope}.week.emptyTitle`, defaultMessage: "Nothing in week {weekNumber}" },
  weekEmptyHint: {
    id: `${scope}.week.emptyHint`,
    defaultMessage: "No acute or planned work orders land in this week. Step to another week, or loosen the filters.",
  },
  // Moving a card to another day, for a manager. The button names the order: a screen reader lists
  // every card's button, and "Move to…" alone would not say which card it belongs to.
  weekMoveAria: { id: `${scope}.week.moveAria`, defaultMessage: "Move {title} to another day" },
  // The button's tooltip and the menu's heading.
  weekMoveTo: { id: `${scope}.week.moveTo`, defaultMessage: "Move to…" },
  // A day in the menu. {day} comes formatted, as "Monday, October 19".
  weekMoveTargetToday: { id: `${scope}.week.moveTargetToday`, defaultMessage: "{day} (today)" },
  // A heading in the menu over each week's days, where they run over more than one week.
  weekMoveWeekHeading: { id: `${scope}.week.moveWeekHeading`, defaultMessage: "Week {weekNumber}" },
  weekMoveDone: { id: `${scope}.week.moveDone`, defaultMessage: "\"{title}\" moved to {day}" },
  weekMoveUndo: { id: `${scope}.week.moveUndo`, defaultMessage: "Undo" },
  weekMoveFailed: { id: `${scope}.week.moveFailed`, defaultMessage: "Could not move the work order" },

  // month overview. It says the rest in the week's words (week.*). The month's name above it is
  // the locale's own ("October 2026").
  monthPrevious: { id: `${scope}.month.previous`, defaultMessage: "Previous month" },
  monthNext: { id: `${scope}.month.next`, defaultMessage: "Next month" },
  monthCurrent: { id: `${scope}.month.current`, defaultMessage: "This month" },
  // The week number at the start of each week, short for a narrow column. The screen reader gets
  // weekAria in its place.
  monthWeekNumber: { id: `${scope}.month.weekNumber`, defaultMessage: "W{weekNumber}" },
  monthWeekAria: { id: `${scope}.month.weekAria`, defaultMessage: "Week {weekNumber}" },
  // Under a day's first cards when there are more than it has room for. It opens the whole week.
  monthMore: { id: `${scope}.month.more`, defaultMessage: "+{count} more" },
  monthShowLess: { id: `${scope}.month.showLess`, defaultMessage: "Show fewer" },
  // A card's tooltip names what the week shows as its panels. The trade's is also what a screen
  // reader hears for a card's trade tag, in the week as in the month.
  monthTooltipDepartment: { id: `${scope}.month.tooltipDepartment`, defaultMessage: "Department: {name}" },
  monthTooltipTrade: { id: `${scope}.month.tooltipTrade`, defaultMessage: "Trade: {name}" },
  // The month's late strip. The first day on screen can be in the month before, so "before the
  // days shown" rather than "before this month".
  monthOverdueTooltip: {
    id: `${scope}.month.overdueTooltip`,
    defaultMessage: "From before the days shown and not done yet, so it is this month's work too",
  },
  monthProjectedLoadFailed: {
    id: `${scope}.month.projectedLoadFailed`,
    defaultMessage: "Could not fetch upcoming work from the recurring schedules. The month shows only work orders that already exist.",
  },
  // {month} comes formatted, as "October 2026".
  monthEmptyTitle: { id: `${scope}.month.emptyTitle`, defaultMessage: "Nothing in {month}" },
  monthEmptyHint: {
    id: `${scope}.month.emptyHint`,
    defaultMessage: "No acute or planned work orders land in this month. Step to another month, or loosen the filters.",
  },
});
